import {
  type DraftContext,
  draftMessagesAtom,
  getDraftKey,
} from "@/store/draftMessagesAtom";
import { useAtomValue, useSetAtom } from "jotai";
import { selectAtom } from "jotai/utils";
import { useCallback, useMemo } from "react";

/**
 * Hook for managing draft messages with automatic persistence.
 * Drafts are persisted to localStorage via Jotai atoms.
 * Optimized to only rerender when the specific draft for this context/patient/encounter changes.
 *
 * @param context - The context type ('chat', 'clinicalNote', 'orders')
 * @param patientId - The patient ID
 * @param encounterId - Optional encounter ID
 * @returns Object with draft message state and handlers
 *
 * @example
 * ```typescript
 * const { draftMessage, setDraftMessage, clearDraftMessage } =
 *   useDraftMessage('chat', patientId, encounterId);
 * ```
 */
export function useDraftMessage(
  context: DraftContext,
  patientId: string,
  encounterId?: string,
) {
  const key = getDraftKey(context, patientId, encounterId);

  const setAllDrafts = useSetAtom(draftMessagesAtom);

  // Select only this specific draft from the atom
  // This ensures we only rerender when THIS draft changes, not when any draft changes
  const draftAtom = useMemo(
    () => selectAtom(draftMessagesAtom, (drafts) => drafts[key]?.text ?? ""),
    [key],
  );

  const draftMessage = useAtomValue(draftAtom);

  /**
   * Update the draft message
   */
  const setDraftMessage = useCallback(
    (text: string) => {
      setAllDrafts((prev) => ({
        ...prev,
        [key]: { text, timestamp: Date.now() },
      }));
    },
    [key, setAllDrafts],
  );

  /**
   * Clear the draft message for this specific context/patient/encounter
   */
  const clearDraftMessage = useCallback(() => {
    setAllDrafts((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, [key, setAllDrafts]);

  return { draftMessage, setDraftMessage, clearDraftMessage };
}
